'use client';

import { type Dispatch, type SetStateAction } from 'react';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface StepExtrasProps {
  asset: ExcursionAsset;
  state: ExcursionWizardState;
  setState: Dispatch<SetStateAction<ExcursionWizardState>>;
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function applyMarkup(price: number, markupRate: number): number {
  return Math.round(price * (1 + markupRate / 100));
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export default function StepExtras({ asset, state, setState }: StepExtrasProps) {
  const markupRate = asset.markupRate ?? 0;
  const extras = asset.extras ?? [];

  const toggleExtra = (key: string) => {
    setState((s) => {
      const has = s.extras.includes(key);
      return {
        ...s,
        extras: has ? s.extras.filter((k) => k !== key) : [...s.extras, key],
      };
    });
  };

  /* ── Subtotale extra selezionati (prezzi cliente) ─────────────────────────── */
  const subtotal = extras.reduce((acc, e) => {
    if (!state.extras.includes(e.key)) return acc;
    const cp = applyMarkup(e.price, markupRate);
    const qty = e.price_type === 'per_person' ? state.participants : 1;
    return acc + cp * qty;
  }, 0);

  return (
    <div data-cid="step-extras" className="flex flex-col gap-4 py-2">
      <p className="text-base font-bold text-slate-800">Extra</p>

      {extras.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">
          Nessun extra disponibile.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {extras.map((e) => {
            const cp = applyMarkup(e.price, markupRate);
            const isChecked = state.extras.includes(e.key);
            const isMandatory = !!e.mandatory;
            const perPerson = e.price_type === 'per_person';
            const lineTotal = cp * (perPerson ? state.participants : 1);

            return (
              <label
                key={e.key}
                data-cid={`step-extras-item-${e.key}`}
                className={[
                  'flex items-start gap-3 px-4 py-3 rounded-xl border transition-all',
                  isMandatory
                    ? 'cursor-default'
                    : 'cursor-pointer',
                  isChecked
                    ? 'border-sky-300 bg-sky-50'
                    : 'border-slate-200 bg-white hover:border-sky-200',
                ].join(' ')}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isMandatory}
                  onChange={() => {
                    if (!isMandatory) toggleExtra(e.key);
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-sky-600"
                  aria-label={e.label}
                />

                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  {/* Riga titolo + badge */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">
                      {e.label}
                    </span>
                    {isMandatory && (
                      <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                        Incluso / Obbligatorio
                      </span>
                    )}
                  </div>

                  {/* Descrizione opzionale */}
                  {e.description && (
                    <p className="text-xs text-slate-400 leading-snug">
                      {e.description}
                    </p>
                  )}

                  {/* Prezzo + subtotale per-person */}
                  <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                    <span className="text-sm font-bold text-sky-700">
                      €{cp}
                      {perPerson && ' /pers'}
                    </span>
                    {perPerson && (
                      <span className="text-xs text-slate-400">
                        × {state.participants} pers = €{lineTotal}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {/* Subtotale extra selezionati */}
      {subtotal > 0 && (
        <div className="flex justify-between items-center border-t border-slate-100 pt-3 mt-1">
          <span className="text-sm text-slate-500">Totale extra selezionati</span>
          <span className="text-base font-bold text-slate-800">€{subtotal}</span>
        </div>
      )}
    </div>
  );
}
